import { useState } from 'react'
import { useToast } from '../context/ToastContext'
import { useAuth } from '../context/AuthContext'
import { isAllScope } from '../lib/platformAdminAccess'
import { reinstateStore, suspendStore, type StoreSuspension } from '../services/storeSuspensionApi'
import { Button } from './ui/Button'
import { Card, CardHeader } from './ui/Card'
import { Modal } from './ui/Modal'

type Props = {
  storeId: string | number
  storeName: string
  suspendedAt: string | null | undefined
  suspensionReason: string | null | undefined
  /** Called with the saved state after a suspend / reinstate. */
  onChanged: (next: StoreSuspension) => void
}

function formatWhen(iso: string) {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return iso
  }
}

/**
 * Super admin: take a store offline (shoppers see "temporarily unavailable") or bring it back.
 * Only full-scope admins can use it; everyone else sees the state read-only.
 */
export function StoreSuspensionPanel({ storeId, storeName, suspendedAt, suspensionReason, onChanged }: Props) {
  const { user } = useAuth()
  const { pushToast } = useToast()
  const canChange = isAllScope(user?.adminScope)
  const suspended = Boolean(suspendedAt)

  const [open, setOpen] = useState<'suspend' | 'reinstate' | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function close() {
    if (busy) return
    setOpen(null)
    setReason('')
    setError(null)
  }

  async function confirm() {
    setBusy(true)
    setError(null)
    try {
      const next = open === 'suspend' ? await suspendStore(storeId, reason) : await reinstateStore(storeId)
      onChanged(next)
      pushToast({
        variant: 'success',
        message:
          open === 'suspend'
            ? `${storeName} is suspended and now shows “temporarily unavailable” to shoppers.`
            : `${storeName} is reinstated and back online.`,
      })
      setOpen(null)
      setReason('')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Card>
        <CardHeader
          title="Store availability"
          description="Suspend a store to take it offline for shoppers. The owner can still sign in to their admin."
          action={
            canChange ? (
              suspended ? (
                <Button type="button" variant="secondary" onClick={() => setOpen('reinstate')}>
                  Reinstate store
                </Button>
              ) : (
                <Button type="button" variant="danger" onClick={() => setOpen('suspend')}>
                  Suspend store
                </Button>
              )
            ) : null
          }
        />
        {suspended ? (
          <div
            className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-foreground"
            role="status"
          >
            <p className="font-semibold">Suspended since {formatWhen(String(suspendedAt))}</p>
            <p className="mt-1 text-muted-foreground">
              Shoppers see “This store is temporarily unavailable”. Browsing, sign-in, checkout, newsletter and contact are all
              blocked.
            </p>
            {suspensionReason ? (
              <p className="mt-2">
                <span className="font-medium">Reason (shown to the store owner):</span> {suspensionReason}
              </p>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">This store is online.</p>
        )}
        {!canChange ? (
          <p className="mt-3 text-xs text-muted-foreground">Only full-scope platform admins can suspend or reinstate stores.</p>
        ) : null}
      </Card>

      <Modal
        open={open !== null}
        onClose={close}
        title={open === 'suspend' ? `Suspend ${storeName}?` : `Reinstate ${storeName}?`}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button type="button" variant={open === 'suspend' ? 'danger' : 'primary'} onClick={() => void confirm()} disabled={busy}>
              {busy ? 'Saving…' : open === 'suspend' ? 'Suspend store' : 'Reinstate store'}
            </Button>
          </div>
        }
      >
        {open === 'suspend' ? (
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              Shoppers will see “This store is temporarily unavailable” straight away: they can’t browse, sign in, order,
              subscribe or send messages. The owner and their team can still sign in to the store admin and will see that the
              store is suspended.
            </p>
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Reason (optional, shown to the store owner)</span>
              <textarea
                className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground shadow-sm"
                rows={3}
                maxLength={500}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Unpaid subscription — contact support to reactivate"
              />
              <span className="block text-right text-[11px] text-muted-foreground">{reason.length}/500</span>
            </label>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {storeName} goes back online for shoppers straight away, and the suspension notice disappears from the owner’s admin.
          </p>
        )}
        {error ? (
          <p className="mt-3 rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
      </Modal>
    </>
  )
}
